/**
 * PagePreviewFrame — Iframe wrapper for the page customizer live preview.
 *
 * Unlike PhoneFrame (mobile-design), this renders a full-width preview
 * without phone bezel. Supports device toggle (desktop/tablet/mobile).
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { Monitor, Tablet, Smartphone, RefreshCw, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

export type PreviewDevice = 'desktop' | 'tablet' | 'mobile'

interface PagePreviewFrameProps {
  src: string | null
  device: PreviewDevice
  tokenLoading?: boolean
  tokenError?: boolean
  onTokenRetry?: () => void
}

const DEVICE_WIDTHS: Record<PreviewDevice, string> = {
  desktop: '100%',
  tablet: '768px',
  mobile: '375px',
}

const LOADING_TIMEOUT = 15_000

export function PagePreviewFrame({
  src,
  device,
  tokenLoading,
  tokenError,
  onTokenRetry,
}: PagePreviewFrameProps) {
  type FrameState = 'loading' | 'loaded' | 'error' | 'no-store'
  const [frameState, setFrameState] = useState<FrameState>('loading')
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (tokenLoading && frameState !== 'loaded') {
      setFrameState('loading')
    } else if (tokenError && frameState !== 'loaded') {
      setFrameState('error')
    } else if (!src && !tokenLoading) {
      setFrameState('no-store')
    }
  }, [tokenLoading, tokenError, src, frameState])

  // Loading timeout
  useEffect(() => {
    if (frameState === 'loading' && src) {
      timeoutRef.current = setTimeout(() => {
        setFrameState((prev) => (prev === 'loading' ? 'error' : prev))
      }, LOADING_TIMEOUT)
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [frameState, src])

  const handleLoad = useCallback(() => {
    setFrameState('loaded')
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
  }, [])

  const handleRetry = useCallback(() => {
    if (onTokenRetry) {
      onTokenRetry()
    }
    setFrameState('loading')
  }, [onTokenRetry])

  const iframeWidth = DEVICE_WIDTHS[device]

  return (
    <div className="flex h-full w-full flex-col items-center justify-center px-4 pb-4">
      {/* Overlay states */}
      {frameState !== 'loaded' && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-50">
          {frameState === 'loading' && (
            <div className="flex flex-col items-center gap-3 text-gray-400">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-sky-500" />
              <span className="text-sm">Loading preview...</span>
            </div>
          )}
          {frameState === 'error' && (
            <div className="flex flex-col items-center gap-3 text-gray-500">
              <AlertCircle className="h-8 w-8 text-gray-300" />
              <span className="text-sm">Preview unavailable</span>
              <button
                onClick={handleRetry}
                className="flex items-center gap-1.5 rounded-xs bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-gray-200 hover:bg-gray-50"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Retry
              </button>
            </div>
          )}
          {frameState === 'no-store' && (
            <div className="flex flex-col items-center gap-2 text-gray-400">
              <Monitor className="h-8 w-8" />
              <span className="text-sm">No storefront configured</span>
            </div>
          )}
        </div>
      )}

      {/* Iframe */}
      {src && (
        <div
          className={cn(
            'relative h-full overflow-hidden rounded-xl shadow-lg transition-all duration-300 ease-out',
            device !== 'desktop' && 'mx-auto ring-1 ring-gray-200',
          )}
          style={{ width: iframeWidth, maxWidth: '100%' }}
        >
          <iframe
            src={src}
            className="h-full w-full bg-white"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            loading="eager"
            onLoad={handleLoad}
          />
        </div>
      )}
    </div>
  )
}

// --- Device toggle bar ---

interface DeviceToggleProps {
  device: PreviewDevice
  onChange: (device: PreviewDevice) => void
}

const DEVICES: Array<{ key: PreviewDevice; icon: typeof Monitor; label: string }> = [
  { key: 'desktop', icon: Monitor, label: 'Desktop' },
  { key: 'tablet', icon: Tablet, label: 'Tablet' },
  { key: 'mobile', icon: Smartphone, label: 'Mobile' },
]

export function DeviceToggle({ device, onChange }: DeviceToggleProps) {
  return (
    <div className="flex items-center gap-1 rounded-sm bg-gray-100 p-1">
      {DEVICES.map(({ key, icon: Icon, label }) => (
        <button
          key={key}
          onClick={() => onChange(key)}
          className={cn(
            'rounded-xs p-1.5 transition-colors',
            device === key
              ? 'bg-white text-sky-500 shadow-sm'
              : 'text-gray-400 hover:text-gray-600',
          )}
          title={label}
        >
          <Icon className="h-4 w-4" />
        </button>
      ))}
    </div>
  )
}
